/* eslint-disable max-classes-per-file */
import moment from 'moment';

import { AnalyticsEvent, ConsentPurpose } from '@rover/types/src/Analytics';

/**
 * Base class for analytics events triggered by multi-select form fields.
 * Tracks the state of all possible options, where each key represents an option
 * and its boolean value indicates whether it's selected.
 *
 * Example: Pet Type selection: { dog: true, cat: false }
 */
export abstract class MultiSelectFieldEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  abstract eventProperties: Record<string, boolean>;
}

/**
 * Base class for analytics events triggered by single-select form fields.
 * Captures the currently selected value as a key-value pair.
 *
 * Example: { serviceType: "dog-walking" }
 */
export abstract class SingleSelectFieldEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  abstract eventProperties: Record<string, string>;
}

/**
 * Base class for analytics events triggered by text input fields.
 * Tracks the current input value entered by the user.
 *
 * Example: Location text field: { value: "Seattle, Washington" }
 */
export abstract class TextFieldEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  abstract eventProperties: { value: string };
}

/**
 * Base class for analytics events triggered by date picker fields.
 * Captures both the start and end dates selected in the UI.
 *
 * Example: { startDateSet: true, endDateSet: false }
 * Example: { startDateSet: true } // end date can be undefined in cases of repeat service selection
 */

export abstract class DatePickerEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  eventProperties: { startDate: string | null; endDate: string | null };

  dateFormat = 'YYYY-MM-DD';

  private formatDate(date: Date | null | undefined): string | null {
    if (date === null || date === undefined) {
      return null;
    }
    return moment(date).format(this.dateFormat);
  }

  constructor(eventProperties: { startDate?: Date | null; endDate?: Date | null }) {
    super(eventProperties);

    const startDate = this.formatDate(eventProperties.startDate);
    const endDate = this.formatDate(eventProperties.endDate);

    this.eventProperties = { ...eventProperties, startDate, endDate };
  }
}

/**
 * Base class for CTA button click events.
 * Provides a `fireEvent` method to ensure the event is reliably fired,
 * even in cases where the CTA leads to a page navigation.
 * This allows tracking events that might otherwise be lost during redirect.
 */
export abstract class CTAButtonClickEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';
}
